/**
 * A CHAIRMAN / DIRECTOR at "All schools" has no school on the request, so every
 * school-scoped read answers 400 "No school associated with this account." /
 * "Pick a school first." / "School context required". Those are not failures
 * the person can act on — the screen already tells them to pick a school — and
 * the interceptor used to turn each one into a red toast (three stacked over a
 * student's sheet header). This recognises them so the interceptor can treat
 * them like a 403 role-gate: log a breadcrumb, stay silent.
 *
 * It is deliberately narrow: only when the caller is an overseer whose active
 * school is NOT a school they hold (none picked, or a stale id the API drops).
 * With a valid school picked, "no school" would be a real bug and still toasts.
 */

const SCOPE_CODES = new Set(["NO_SCHOOL", "NO_SCHOOL_CONTEXT", "SCHOOL_REQUIRED"]);
const SCOPE_MESSAGE = /^(no school|school context required|schoolid is required|pick a school|select a school)/i;

/** Is this a 400 whose only complaint is "there is no school on this request"? */
export function isSchoolScopeRefusal(status: number | undefined, data: unknown): boolean {
  if (status !== 400) return false;
  const body = data as { error?: unknown } | null | undefined;
  const err = body?.error;
  if (typeof err === "string") return SCOPE_MESSAGE.test(err.trim());
  const e = err as { code?: unknown; message?: unknown } | null | undefined;
  if (typeof e?.code === "string" && SCOPE_CODES.has(e.code)) return true;
  return typeof e?.message === "string" && SCOPE_MESSAGE.test(e.message.trim());
}

/** Overseer with no granted school in scope (All schools, or an id they no longer hold). */
export function overseerHasNoSchoolScope(
  user: { role?: string; schools?: Array<{ id: string }> | null } | null | undefined,
  activeSchoolId: string | null | undefined,
): boolean {
  if (user?.role !== "CHAIRMAN" && user?.role !== "DIRECTOR") return false;
  if (!activeSchoolId || activeSchoolId === "platform") return true;
  const granted = user.schools ?? [];
  return granted.length > 0 && !granted.some((g) => g.id === activeSchoolId);
}
